/**
 * Snapshot of the ordered pre-order ID list currently displayed by
 * `PreOrderListView` on `/recap`. Published by the list, consumed by the
 * focus view at `/recap/:id` to drive the prev/next chevrons + counter
 * in its header.
 *
 * The focus view's chevrons used to read `filtering.filteredPreOrders`
 * (the outer toolbar-filter slice), which doesn't reflect the column-
 * header filters, search, sort, and partner-grouping that
 * `PreOrderListView` layers on top — so the counter could disagree with
 * what the user just clicked through. This store captures the actual
 * final ordered list (`grouped`) the list view is rendering, and freezes
 * it for the focus view's lifetime: the snapshot is only overwritten
 * when `PreOrderListView` (re)mounts on `/recap` and publishes again.
 *
 * Cold deep-link entries (refresh on `/recap/:id`, bookmarks, links from
 * other routes) find an empty snapshot — by design — and the focus view
 * hides the chevrons, since "the list I just left" doesn't exist.
 *
 * Singleton + `useSyncExternalStore`, mirroring `confirmInFlightStore`.
 * No context wrapping: there's a single list view in the shell.
 */

import { useSyncExternalStore } from "react";

type Listener = () => void;

const EMPTY_IDS: readonly string[] = Object.freeze([]);

class PreOrderListSnapshotStore {
  private ids: readonly string[] = EMPTY_IDS;
  private readonly listeners = new Set<Listener>();

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getIds = (): readonly string[] => this.ids;

  setIds(next: readonly string[]): void {
    // Shallow equality dedup so that filter/sort recomputes that produce
    // an identical ordering don't notify subscribers (i.e. the focus view
    // doesn't re-render on every keystroke in the list-view search box
    // when the resulting order hasn't changed).
    if (sameOrder(this.ids, next)) return;
    this.ids = next.length === 0 ? EMPTY_IDS : next;
    for (const l of this.listeners) l();
  }

  clear(): void {
    if (this.ids.length === 0) return;
    this.ids = EMPTY_IDS;
    for (const l of this.listeners) l();
  }
}

function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

const preOrderListSnapshotStore = new PreOrderListSnapshotStore();

/** Imperative API for non-React callers and tests. */
export const preOrderListSnapshot = {
  setIds: (next: readonly string[]): void => {
    preOrderListSnapshotStore.setIds(next);
  },
  clear: (): void => {
    preOrderListSnapshotStore.clear();
  },
  getIds: (): readonly string[] => preOrderListSnapshotStore.getIds(),
  /** Test/integration hook for the same subscribe channel React uses. */
  subscribe: (listener: () => void): (() => void) =>
    preOrderListSnapshotStore.subscribe(listener),
};

/**
 * Subscribe to the current snapshot of pre-order IDs. Returns the same
 * frozen reference across renders when the order hasn't changed, so
 * consumers' `useMemo` dependencies stay stable.
 *
 * The `getServerSnapshot` arg returns the same `EMPTY_IDS` constant the
 * store starts with, so a server render (or any non-client environment)
 * sees an empty snapshot without triggering React's "snapshot returned
 * by `getServerSnapshot` should be cached" warning.
 */
export function usePreOrderListSnapshotIds(): readonly string[] {
  return useSyncExternalStore(
    preOrderListSnapshotStore.subscribe,
    preOrderListSnapshotStore.getIds,
    () => EMPTY_IDS,
  );
}
